import { forwardRef } from 'react';
import { Formik, Form } from 'formik';
import { useSdk } from '@packlink/packlink-sdk-provider';
import { useTranslation } from '@packlink/translation-provider';
import { Typography, Button, ButtonSize } from '@shipengine/giger';
import { ClientType, Application } from '@packlink/packlink-sdk';
import InvoiceCompanyForm from './InvoiceCompanyForm.js';
import InvoiceAddressForm from './InvoiceAddressForm.js';
import InvoiceContactForm from './InvoiceContactForm.js';
import InvoiceAutonomousForm from './InvoiceAutonomousForm.js';
import { getFormStyles, getTitleStyles, getFormColumnStyles, getFormColumLabelStyles, getSubmitNotBlockWrapperStyles, getSubmitNotBlockButtonStyles } from './MainFormStyles.js';
import { usePhoneNumberVerification } from '../hooks/usePhoneNumberVerification.js';
import { useCountryPrefixOptions } from '../hooks/useCountryPrefixOptions.js';
import { jsx, jsxs } from '@emotion/react/jsx-runtime';
import { parseClientOrAddressToFormData, parseFormDataToClient } from '../utils/transformData.js';
import { getInvoiceValidation } from '../utils/InvoiceFormValidations.js';
import { useAddressValues } from '../contexts/addressValues.js';

const MainForm = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    title,
    config,
    type,
    cdnUrl,
    client,
    from,
    buttonStyle,
    validation,
    showSubmitButton = true,
    requiresPhoneNumberVerification,
    isPhoneNumberVerified,
    callback,
    onSuccess,
    onFailure,
    ...rest
  } = _ref;
  const {
    t
  } = useTranslation();
  const {
    selectedPostalCode,
    selectedCountry
  } = useAddressValues();
  const {
    showDefaultPhoneInput,
    showVerificationPhoneInput
  } = usePhoneNumberVerification(requiresPhoneNumberVerification, isPhoneNumberVerified);
  const {
    options: phonePrefixOptions,
    defaultValue: defaultPrefixValue
  } = useCountryPrefixOptions(cdnUrl, client === null || client === void 0 ? void 0 : client.alpha2Code, showVerificationPhoneInput);
  const isBusinessBlock = type === ClientType.BUSINESS;
  const sdk = useSdk();
  const initialValues = parseClientOrAddressToFormData(client, from, showDefaultPhoneInput, defaultPrefixValue);
  const validationSchema = () => getInvoiceValidation(t, type, showDefaultPhoneInput);
  const onSubmit = (form, formikHelpers) => {
    if (callback) {
      return callback(form);
    } else {
      const parsedClient = parseFormDataToClient(form, type, selectedPostalCode, selectedCountry, client);
      const clients = sdk.getApplication() === Application.CHECKOUT ? sdk.com.clients : sdk.v1.clients;
      return clients.update(parsedClient).then(data => {
        if (data.companyName) {
          parsedClient.companyName = data.companyName;
          formikHelpers.setFieldValue('companyName', data.companyName);
        }
        return onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess(data, parsedClient, form.phone);
      }).catch(onFailure);
    }
  };
  return jsx(Formik, {
    onSubmit: onSubmit,
    initialValues: initialValues,
    validationSchema: validationSchema,
    validateOnBlur: validation === null || validation === void 0 ? void 0 : validation.validateOnBlur,
    validateOnChange: validation === null || validation === void 0 ? void 0 : validation.validateOnChange,
    enableReinitialize: true,
    children: jsxs(Form, {
      css: getFormStyles(config === null || config === void 0 ? void 0 : config.twoColumns),
      role: "form",
      ref: ref,
      ...rest,
      children: [title && jsx(Typography, {
        css: getTitleStyles,
        component: "h3",
        variant: "heading2",
        children: title
      }), jsxs("fieldset", {
        css: getFormColumnStyles(config === null || config === void 0 ? void 0 : config.twoColumns),
        children: [(config === null || config === void 0 ? void 0 : config.showLegends) && jsx("legend", {
          css: getFormColumLabelStyles,
          children: t('invoice-form.label.column1')
        }), isBusinessBlock ? jsx(InvoiceCompanyForm, {}) : jsx(InvoiceAutonomousForm, {}), jsx(InvoiceContactForm, {
          cdnUrl: cdnUrl,
          orientation: config === null || config === void 0 ? void 0 : config.contactFormOrientation,
          phonePrefixOptions: phonePrefixOptions,
          requiresPhoneNumberVerification: requiresPhoneNumberVerification,
          isPhoneNumberVerified: requiresPhoneNumberVerification && isPhoneNumberVerified
        })]
      }), jsxs("fieldset", {
        css: getFormColumnStyles(config === null || config === void 0 ? void 0 : config.twoColumns),
        children: [(config === null || config === void 0 ? void 0 : config.showLegends) && jsx("legend", {
          css: getFormColumLabelStyles,
          children: t('invoice-form.label.column2')
        }), jsx(InvoiceAddressForm, {})]
      }), showSubmitButton && jsx("div", {
        css: !(buttonStyle !== null && buttonStyle !== void 0 && buttonStyle.block) && getSubmitNotBlockWrapperStyles,
        children: jsx(Button, {
          isFullWidth: true,
          type: "submit",
          size: ButtonSize.LARGE,
          variant: buttonStyle === null || buttonStyle === void 0 ? void 0 : buttonStyle.variant,
          css: !(buttonStyle !== null && buttonStyle !== void 0 && buttonStyle.block) && getSubmitNotBlockButtonStyles,
          children: t('invoice-form.label.save-button')
        })
      })]
    })
  });
});
MainForm.displayName = 'MainForm';
var MainForm$1 = MainForm;

export { MainForm$1 as default };
//# sourceMappingURL=MainForm.js.map
